// components/landing-header.tsx
'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import clsx from 'clsx';
import { ModeToggle } from '@/components/layout/mode-toggle';

export function LandingHeader() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const pathname = usePathname();

    // Close mobile menu on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [pathname]);

    // Lock body scroll when menu is open
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileOpen]);

    // Close on ESC
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMobileOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-8">
                {/* Logo */}
                <Link
                    href="/"
                    className="flex items-center gap-2 font-bold text-lg tracking-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                    <span className="bg-gradient-to-r from-indigo-500 to-teal-500 dark:from-indigo-400 dark:to-teal-400 bg-clip-text text-transparent">
                        KennyDB
                    </span>
                    <span aria-hidden>👞</span>
                </Link>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600 dark:text-slate-300">
                    <NavLink href="#features">Funzionalità</NavLink>
                    <NavLink href="#testimonials">Dicono di noi</NavLink>
                    <NavLink href="/blog">Notizie & Blog</NavLink>
                    <NavLink href="/pro-features">Funzioni Pro</NavLink>
                    <NavLink href="/pricing">Abbonamenti</NavLink>

                    <ModeToggle />

                    {/* System Status Indicator */}
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800">
                        <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Sistema Online</span>
                    </div>

                    <Link href="/sign-in">
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            Accedi
                        </Button>
                    </Link>

                    <Link href="/sign-up">
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white dark:bg-indigo-600 dark:hover:bg-indigo-500">
                            Inizia Gratis
                        </Button>
                    </Link>
                </nav>

                {/* Mobile Toggle */}
                <div className="md:hidden flex items-center gap-2">
                    <ModeToggle />
                    <button
                        aria-label="Apri menu"
                        aria-expanded={mobileOpen}
                        aria-controls="mobile-menu"
                        onClick={() => setMobileOpen(v => !v)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                        {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <div
                id="mobile-menu"
                className={clsx(
                    'md:hidden fixed inset-0 top-16 z-40 bg-white dark:bg-slate-950 transition-transform duration-300',
                    mobileOpen ? 'translate-x-0' : 'translate-x-full'
                )}
            >
                <nav className="flex h-full flex-col gap-4 px-6 py-8 text-base font-medium text-slate-600 dark:text-slate-300">
                    {/* Mobile System Status */}
                    <div className="flex items-center gap-2 px-2 py-2 rounded-md bg-slate-100 dark:bg-slate-800 mb-2">
                        <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Stato Sistema: Online</span>
                    </div>

                    <MobileLink href="#features">Funzionalità</MobileLink>
                    <MobileLink href="#testimonials">Dicono di noi</MobileLink>
                    <MobileLink href="/blog">Notizie & Blog</MobileLink>
                    <MobileLink href="/pro-features">Funzioni Pro</MobileLink>
                    <MobileLink href="/pricing">Abbonamenti</MobileLink>

                    <div className="mt-6 flex flex-col gap-3">
                        <Link href="/sign-in">
                            <Button
                                variant="outline"
                                className="w-full border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                            >
                                Accedi
                            </Button>
                        </Link>
                        <Link href="/sign-up">
                            <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white dark:bg-indigo-600 dark:hover:bg-indigo-500">
                                Inizia Gratis
                            </Button>
                        </Link>
                    </div>
                </nav>
            </div>
        </header>
    );
}

/* ---------------------------------- */
/* Helpers */

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
    return (
        <Link
            href={href}
            className="hover:text-slate-900 dark:hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
            {children}
        </Link>
    );
}

function MobileLink({ href, children }: { href: string; children: React.ReactNode }) {
    return (
        <Link
            href={href}
            className="rounded-md px-2 py-2 hover:bg-slate-100 dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
            {children}
        </Link>
    );
}
